import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import styled, { keyframes } from "styled-components";
import { Container } from "react-bootstrap";

/*
 * Methodology — 3 phases on a marching dot-matrix track.
 *
 * Nothing-Design vibe: the line between phases is a dotted trail that
 * never stops moving (slow leftward drift), each phase is a dot-matrix
 * 3x3 square (LED-style), title + duration + body sit below each node.
 * No "01/02/03" labels — the position on the track IS the order.
 */

const Section = styled.section`
  padding: 96px 0;
  background: var(--nd-surface);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0 0 64px;
`;

const Stepper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-left: 56px;
  }
`;

/* SVG with the marching dotted track. Nodes are separate SVGs that
 * sit on top — keeps dot-matrix shapes round regardless of viewport. */
const TrackSvg = styled.svg`
  position: absolute;
  top: 14px;
  left: 0;
  right: 0;
  width: 100%;
  height: 28px;
  pointer-events: none;
  overflow: visible;

  @media (max-width: 768px) {
    top: 0;
    left: -56px;
    right: auto;
    width: 28px;
    height: 100%;
  }
`;

/* Per-node positioning wrapper — absolute % position on the track. */
const NodeAnchor = styled.div`
  position: absolute;
  top: 0;
  width: 28px;
  height: 28px;
  transform: translateX(-50%);
  pointer-events: none;

  @media (max-width: 768px) {
    top: ${({ pct }) => `${pct}%`};
    left: -56px;
    transform: translateY(-50%);
  }
`;

const Name = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0 0 6px;
`;

const Duration = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  margin: 0 0 12px;
`;

const Desc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0;
`;

const ColumnInner = styled.div`
  max-width: 240px;

  @media (max-width: 768px) {
    max-width: none;
    padding-left: 8px;
  }
`;

const LearnMore = styled(Link)`
  display: inline-block;
  margin-top: 48px;
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-display) !important;
  text-decoration: none !important;
  &::after { content: ' →'; }
  &:hover { opacity: 0.75; }
`;

/* Marching dot animation — drift the dashed pattern leftward forever.
 * Nothing's LED-style: percussive, never urgent. 8s per full cycle.
 */
const march = keyframes`
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -16; }
`;

const marchV = keyframes`
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -16; }
`;

const TrackLine = styled.line.attrs({ vectorEffect: "non-scaling-stroke" })`
  stroke: var(--nd-text-disabled);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: 2 6;
  animation: ${march} 1.4s linear infinite;
  opacity: ${({ visible }) => (visible ? 0.85 : 0)};
  transition: opacity 500ms cubic-bezier(0.25, 0.1, 0.25, 1);
`;

const TrackLineV = styled.line.attrs({ vectorEffect: "non-scaling-stroke" })`
  stroke: var(--nd-text-disabled);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: 2 6;
  animation: ${marchV} 1.4s linear infinite;
  opacity: ${({ visible }) => (visible ? 0.85 : 0)};
  transition: opacity 500ms cubic-bezier(0.25, 0.1, 0.25, 1);
`;

const PHASES = [
  {
    title: "Audit",
    body:
      "We map your infra, costs, and the gaps. You get a one-pager you can show your board in week one. No proposal yet.",
    duration: "Week 1",
  },
  {
    title: "Build",
    body:
      "Senior engineers embed into your team and fix the things the audit flagged first — CI/CD, K8s, IaC, observability.",
    duration: "Months 1–3",
  },
  {
    title: "Operate",
    body:
      "We hand over runbooks and on-call rotations. Optional juniors stay long-term at a reduced rate — your team owns the stack.",
    duration: "Ongoing",
  },
];

// Node x positions (% of width in viewBox). Centered in each of 3 equal columns.
const NODE_X = [16.66, 50, 83.33];

const TRANSITION = "cubic-bezier(0.25, 0.1, 0.25, 1)";

/* Dot-matrix node: standalone 28x28 SVG with a 3x3 grid of dots.
 * Each dot fades + scales in with a small stagger on intersection.
 * Lives in its own SVG so shape stays round (parent track SVG is
 * stretched with preserveAspectRatio=none and would warp circles). */
const DotMatrixNode = ({ visible, delay }) => {
  const positions = [4, 14, 24]; // 3 columns/rows inside 28px box
  const cells = [];
  let i = 0;
  for (const cy of positions) {
    for (const cx of positions) {
      const cellDelay = delay + i * 25;
      cells.push(
        <circle
          key={`${cx}:${cy}`}
          cx={cx}
          cy={cy}
          r={2}
          fill="var(--nd-text-display)"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "scale(1)" : "scale(0.3)",
            transformOrigin: `${cx}px ${cy}px`,
            transformBox: "fill-box",
            transition: `opacity 220ms ${TRANSITION} ${cellDelay}ms, transform 220ms ${TRANSITION} ${cellDelay}ms`,
          }}
        />,
      );
      i += 1;
    }
  }
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      {cells}
    </svg>
  );
};

const Methodology = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
    } else if (mq.addListener) {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", update);
      } else if (mq.removeListener) {
        mq.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const columnStyle = (i) => ({
    paddingTop: isMobile ? 0 : 64,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(8px)",
    transition: `opacity 400ms ${TRANSITION} ${i * 120}ms, transform 400ms ${TRANSITION} ${i * 120}ms`,
  });

  return (
    <Section ref={ref}>
      <Container>
        <SectionLabel>How we work</SectionLabel>
        <SectionTitle>Audit. Build. Operate.</SectionTitle>

        <Stepper>
          <TrackSvg
            preserveAspectRatio="none"
            viewBox={isMobile ? "0 0 28 100" : "0 0 100 28"}
            aria-hidden="true"
          >
            {isMobile ? (
              <TrackLineV x1="14" y1="14" x2="14" y2="86" visible={visible ? 1 : 0} />
            ) : (
              <TrackLine x1={NODE_X[0]} y1="14" x2={NODE_X[2]} y2="14" visible={visible ? 1 : 0} />
            )}
          </TrackSvg>

          {NODE_X.map((pctX, i) => {
            const pctY = isMobile ? (i === 0 ? 14 : i === 1 ? 50 : 86) : null;
            return (
              <NodeAnchor
                key={i}
                pct={pctY}
                style={isMobile ? {} : { left: `${pctX}%` }}
              >
                <DotMatrixNode visible={visible} delay={i * 180} />
              </NodeAnchor>
            );
          })}

          {PHASES.map((p, i) => (
            <div key={p.title} style={columnStyle(i)}>
              <ColumnInner>
                <Name>{p.title}</Name>
                <Duration>{p.duration}</Duration>
                <Desc>{p.body}</Desc>
              </ColumnInner>
            </div>
          ))}
        </Stepper>

        <LearnMore to="/how-we-work">See the long version</LearnMore>
      </Container>
    </Section>
  );
};

export default Methodology;
